import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  CircularProgress,
  FormHelperText,
  Snackbar,
  Alert,
  SelectChangeEvent,
  AlertColor,
  Container
} from '@mui/material';

import AuthLayout from '../components/AuthLayout';
import assetService from '../services/asset.service';
import clientService from '../services/client.service';
import categoryService from '../services/category.service';
import { Asset } from '../services/asset.service';
import { Client } from '../services/client.service';
import { Category } from '../services/category.service';

type AssetStatus = 'active' | 'maintenance' | 'inactive' | 'retired';

interface AssetFormData {
  name: string;
  description: string;
  serialNumber: string;
  model: string;
  manufacturer: string;
  purchaseDate: string;
  warrantyExpirationDate: string;
  status: AssetStatus;
  clientId: string;
  categoryId: string;
  location: string;
  latitude: string;
  longitude: string;
  notes: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

const initialFormData: AssetFormData = {
  name: '',
  description: '',
  serialNumber: '',
  model: '',
  manufacturer: '',
  purchaseDate: '',
  warrantyExpirationDate: '',
  status: 'active',
  clientId: '',
  categoryId: '',
  location: '',
  latitude: '',
  longitude: '',
  notes: ''
};

const AssetForm: React.FC = () => {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  const [formData, setFormData] = useState<AssetFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [errors, setErrors] = useState<Partial<Record<keyof AssetFormData, string>>>({});

  const isEditMode = Boolean(assetId);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const clientsResponse = await clientService.getClients();
        const categoriesResponse = await categoryService.getCategories();
        
        if (clientsResponse.clients) {
          setClients(clientsResponse.clients);
        }
        
        if (categoriesResponse.categories) {
          setCategories(categoriesResponse.categories);
        }

        if (assetId) {
          const response = await assetService.getAssetById(assetId);
          
          if (response.asset) {
            const asset = response.asset;
            
            const purchaseDate = asset.purchaseDate ? asset.purchaseDate.split('T')[0] : '';
            // @ts-ignore - Propriedade warrantyExpirationDate não está definida no tipo Asset
            const warrantyExpirationDate = asset.warrantyExpirationDate ? asset.warrantyExpirationDate.split('T')[0] : '';
            
            // Extrair o clientId corretamente (pode ser string ou objeto)
            let clientId = '';
            if (typeof asset.client === 'string') {
              clientId = asset.client;
            } else if (asset.client && typeof asset.client === 'object') {
              // @ts-ignore
              clientId = asset.client._id || asset.client.id || '';
            }
            
            // Buscar o ID da categoria pelo nome
            let categoryId = '';
            if (asset.category && typeof asset.category === 'string') {
              // asset.category é o nome da categoria, precisamos encontrar o ID
              const foundCategory = categoriesResponse.categories?.find(
                (cat: any) => cat.name === asset.category
              );
              categoryId = foundCategory?._id || '';
              console.log('Categoria encontrada:', foundCategory);
            } else if (asset.category && typeof asset.category === 'object') {
              // @ts-ignore
              categoryId = asset.category._id || asset.category.id || '';
            }
            
            const newFormData = {
              name: asset.name || '',
              description: asset.description || '',
              serialNumber: asset.serialNumber || '',
              model: asset.model || '',
              manufacturer: asset.manufacturer || '',
              purchaseDate,
              warrantyExpirationDate,
              status: asset.status || 'active',
              clientId,
              categoryId,
              // @ts-ignore - Propriedade location não está definida no tipo Asset
              location: asset.location || '',
              // @ts-ignore
              latitude: asset.latitude ? String(asset.latitude) : '',
              // @ts-ignore
              longitude: asset.longitude ? String(asset.longitude) : '',
              notes: asset.notes || ''
            };
            
            console.log('FormData com categoryId:', newFormData.categoryId);
            setFormData(newFormData);
          } else if (response.error) {
            setSnackbar({
              open: true,
              message: response.error,
              severity: 'error'
            });
            navigate('/assets');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setSnackbar({
          open: true,
          message: 'Error loading data. Please try again.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [assetId, navigate]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof AssetFormData, string>> = {};
    let isValid = true;

    // Validações de campos obrigatórios
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
      isValid = false;
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Nome deve ter pelo menos 3 caracteres';
      isValid = false;
    }

    if (!formData.serialNumber.trim()) {
      newErrors.serialNumber = 'Número de série é obrigatório';
      isValid = false;
    }

    if (!formData.clientId) {
      newErrors.clientId = 'Cliente é obrigatório';
      isValid = false;
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'Categoria é obrigatória';
      isValid = false;
    }
    
    // Validações de datas
    if (formData.purchaseDate && formData.warrantyExpirationDate) {
      const purchaseDate = new Date(formData.purchaseDate);
      const warrantyDate = new Date(formData.warrantyExpirationDate);
      
      if (warrantyDate < purchaseDate) {
        newErrors.warrantyExpirationDate = 'A data de expiração da garantia não pode ser anterior à data de compra';
        isValid = false;
      }
    }
    
    // Validação de modelo e fabricante
    if (formData.model.trim() && !formData.manufacturer.trim()) {
      newErrors.manufacturer = 'Informe o fabricante quando o modelo for especificado';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      if (errors[name as keyof AssetFormData]) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    if (name) {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      if (errors[name as keyof AssetFormData]) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const apiData: any = {
        name: formData.name,
        description: formData.description,
        serialNumber: formData.serialNumber,
        model: formData.model,
        manufacturer: formData.manufacturer,
        purchaseDate: formData.purchaseDate || undefined,
        warrantyExpirationDate: formData.warrantyExpirationDate || undefined,
        status: formData.status,
        categoryId: formData.categoryId,
        client: formData.clientId,
        location: formData.location,
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
        notes: formData.notes,
        photos: [],
        documents: [],
        isActive: true
      };
      
      console.log('Dados que serão enviados ao backend:', apiData);
      
      if (assetId) {
        const response = await assetService.updateAsset(assetId, apiData);
        console.log('Resposta ao atualizar ativo:', response);
        
        if (response.success !== false) {
          // Sucesso - o backend retornou uma resposta positiva
          setSnackbar({
            open: true,
            message: response.message || 'Ativo atualizado com sucesso',
            severity: 'success'
          });
          navigate('/assets');
        } else {
          // Erro - o backend retornou um erro
          console.error('Erro ao atualizar ativo:', response);
          console.error('Mensagem de erro:', response.error);
          
          // Formatar a mensagem de erro para melhor legibilidade
          let errorMessage = response.error || 'Falha ao atualizar ativo. Verifique os dados e tente novamente.';
          
          // Tentar detectar se a mensagem é um JSON stringificado e formatá-la
          try {
            if (errorMessage.startsWith('{') || errorMessage.startsWith('[')) {
              const parsedError = JSON.parse(errorMessage);
              errorMessage = JSON.stringify(parsedError, null, 2);
            }
          } catch (e) {
            // Se não for um JSON válido, manter a mensagem original
          }
          
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error'
          });
        }
      } else {
        const response = await assetService.createAsset(apiData);
        console.log('Resposta ao criar ativo:', response);
        
        if (response.success !== false) {
          // Sucesso - o backend retornou uma resposta positiva
          setSnackbar({
            open: true,
            message: response.message || 'Ativo criado com sucesso',
            severity: 'success'
          });
          navigate('/assets');
        } else {
          // Erro - o backend retornou um erro
          console.error('Erro ao criar ativo:', response);
          console.error('Mensagem de erro:', response.error);
          
          // Formatar a mensagem de erro para melhor legibilidade
          let errorMessage = response.error || 'Falha ao criar ativo. Verifique os dados e tente novamente.';
          
          // Tentar detectar se a mensagem é um JSON stringificado e formatá-la
          try {
            if (errorMessage.startsWith('{') || errorMessage.startsWith('[')) {
              const parsedError = JSON.parse(errorMessage);
              errorMessage = JSON.stringify(parsedError, null, 2);
            }
          } catch (e) {
            // Se não for um JSON válido, manter a mensagem original
          }
          
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error'
          });
        }
      }
    } catch (error: any) {
      console.error('Erro ao enviar formulário:', error);
      console.error('Detalhes do erro:', {
        message: error.message,
        response: error.response,
        data: error.response?.data,
        status: error.response?.status
      });
      
      // Tentar extrair mensagem de erro mais detalhada
      let errorMessage = 'Ocorreu um erro. Por favor, tente novamente.';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error, null, 2);
        } else if (errorData.details) {
          errorMessage = typeof errorData.details === 'string' ? errorData.details : JSON.stringify(errorData.details, null, 2);
        } else {
          // Tentar usar o objeto de erro completo
          errorMessage = JSON.stringify(errorData, null, 2);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Verificar se a mensagem é um erro de rede
      if (error.message && error.message.includes('Network Error')) {
        errorMessage = 'Erro de conexão com o servidor. Verifique se o backend está em execução.';
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout title="Carregando Ativo...">
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
            <CircularProgress size={60} thickness={4} />
          </Box>
        </Container>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Formulário de Ativo">
      <Box sx={{ padding: 3 }}>
        <Paper elevation={3} sx={{ overflow: 'hidden' }}>
          <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', p: 2, mb: 3 }}>
            <Typography variant="h5">{isEditMode ? 'Editar Ativo' : 'Novo Ativo'}</Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 1 }}>
                    Informações Básicas
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nome"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Número de Série"
                    name="serialNumber"
                    value={formData.serialNumber}
                    onChange={handleChange}
                    error={!!errors.serialNumber}
                    helperText={errors.serialNumber}
                    required
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descrição"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                    Especificações Técnicas
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Modelo"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fabricante"
                    name="manufacturer"
                    value={formData.manufacturer}
                    onChange={handleChange}
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                    Datas Importantes
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Data de Compra"
                    name="purchaseDate"
                    type="date"
                    value={formData.purchaseDate}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Data de Expiração da Garantia"
                    name="warrantyExpirationDate"
                    type="date"
                    value={formData.warrantyExpirationDate}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                    Classificação e Status
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.clientId} size="medium">
                    <InputLabel>Cliente</InputLabel>
                    <Select
                      name="clientId"
                      value={formData.clientId}
                      onChange={handleSelectChange}
                      label="Cliente"
                    >
                      {clients.map(client => (
                        <MenuItem key={client._id} value={client._id}>
                          {client.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.clientId && <FormHelperText>{errors.clientId}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.categoryId} size="medium">
                    <InputLabel>Categoria</InputLabel>
                    <Select
                      name="categoryId"
                      value={formData.categoryId}
                      onChange={handleSelectChange}
                      label="Categoria"
                    >
                      {categories.map((category) => (
                        <MenuItem key={category._id} value={category._id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.categoryId && <FormHelperText>{errors.categoryId}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="medium">
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={handleSelectChange}
                      label="Status"
                    >
                      <MenuItem value="active">Ativo</MenuItem>
                      <MenuItem value="maintenance">Em Manutenção</MenuItem>
                      <MenuItem value="inactive">Inativo</MenuItem>
                      <MenuItem value="retired">Aposentado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={12}>
                  <TextField
                    fullWidth
                    label="Localização"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    size="medium"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Latitude"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleChange}
                    size="medium"
                    type="number"
                    inputProps={{ step: "any", min: -90, max: 90 }}
                    placeholder="-23.550520"
                    helperText="Coordenada de latitude (-90 a 90)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Longitude"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleChange}
                    size="medium"
                    type="number"
                    inputProps={{ step: "any", min: -180, max: 180 }}
                    placeholder="-46.633308"
                    helperText="Coordenada de longitude (-180 a 180)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                    Informações Adicionais
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Observações"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    size="medium"
                    placeholder="Adicione informações complementares sobre o ativo"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <Button 
                      variant="outlined" 
                      onClick={() => navigate('/assets')}
                      disabled={submitting}
                      startIcon={<span className="material-icons">arrow_back</span>}
                      sx={{ px: 3 }}
                    >
                      Voltar
                    </Button>
                    <Button 
                      type="submit" 
                      variant="contained" 
                      color="primary"
                      disabled={submitting}
                      startIcon={submitting ? undefined : <span className="material-icons">{isEditMode ? 'save' : 'add'}</span>}
                      sx={{ px: 4 }}
                    >
                      {submitting ? <CircularProgress size={24} /> : isEditMode ? 'Atualizar' : 'Criar'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Paper>
      </Box>
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={snackbar.severity === 'error' ? 10000 : 6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ 
            width: '100%', 
            maxWidth: '600px',
            '& .MuiAlert-message': {
              maxHeight: '200px',
              overflowY: 'auto',
              whiteSpace: 'pre-line'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AuthLayout>
  );
};

export default AssetForm;